import React, { useState } from 'react';
import { FaPlus, FaTimes, FaStar, FaRegStar, FaPen } from 'react-icons/fa';
import { useConfirm } from '../contexts/ConfirmContext';
import AdliTatilSecici from './AdliTatilSecici';
import SureSecimi from './SureSecimi';
import { tumPresetler } from './presetler';
import { REJIM_KISA } from './adliTatil';
import { birimAdi } from './ayarlar';

/** Tek preset satırı — favori yıldızı + (kullanıcı preset'iyse) sil. */
function PresetSatir({ p, favori, onFavori, onSil }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2 pl-2.5 pr-1.5 text-sm">
      {p.manuel ? (
        <FaPen className="h-2.5 w-2.5 flex-none text-slate-400" />
      ) : (
        <span
          className={`h-2.5 w-2.5 flex-none rounded-full ${
            p.adliTatil === 'cmk' ? 'bg-indigo-500' : p.adliTatil === 'hmk' ? 'bg-emerald-500' : 'bg-slate-300'
          }`}
        />
      )}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-semibold text-slate-800">{p.ad}</span>
          {p.rozet ? (
            <span className="flex-none rounded-full bg-amber-50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
              {p.rozet}
            </span>
          ) : null}
        </span>
        <span className="truncate text-[11px] text-slate-400">
          {p.manuel ? (
            'Süreyi elle gir'
          ) : (
            <>
              <span className="app-num">{p.sure} {birimAdi(p.birim)}</span> · {REJIM_KISA[p.adliTatil]}
              {p.dayanak ? ` · ${p.dayanak}` : ''}
            </>
          )}
        </span>
      </span>
      <button
        type="button"
        onClick={onFavori}
        className="ml-auto flex h-7 w-7 flex-none items-center justify-center rounded-lg transition-colors hover:bg-amber-50"
        title={favori ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        aria-label={favori ? 'Favorilerden çıkar' : 'Favorilere ekle'}
      >
        {favori ? <FaStar className="h-3.5 w-3.5 text-amber-400" /> : <FaRegStar className="h-3.5 w-3.5 text-slate-300" />}
      </button>
      {onSil ? (
        <button
          type="button"
          onClick={onSil}
          className="flex h-7 w-7 flex-none items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
          aria-label="Sil"
          title="Sil"
        >
          <FaTimes className="h-3 w-3" />
        </button>
      ) : null}
    </li>
  );
}

/**
 * Hazır süre yönetimi (Ayarlar penceresinde). Yerleşik + "Manuel" + kullanıcı presetleri listelenir;
 * her birine yıldızla favori eklenir (ana ekranda yalnızca favoriler görünür). Kullanıcı kendi
 * preset'ini ad + süre + birim + adli tatil rejimi seçerek ekler/siler.
 *
 * Props: kullaniciPresetleri, favoriler (id[]), onEkle(ad, sure, birim, adliTatil), onSil(id), onFavoriTutle(id).
 */
function PresetYonetici({ kullaniciPresetleri, favoriler, onEkle, onSil, onFavoriTutle }) {
  const [ad, setAd] = useState('');
  const [sure, setSure] = useState('15');
  const [birim, setBirim] = useState('gun');
  const [rejim, setRejim] = useState('kapali');
  const [uyari, setUyari] = useState('');

  const liste = tumPresetler(kullaniciPresetleri);
  const confirm = useConfirm();

  const silOnayli = async (p) => {
    const onay = await confirm({
      title: 'Hazır süre silinsin mi?',
      message: `"${p.ad}" kaldırılacak.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      tone: 'danger',
    });
    if (onay) onSil(p.id);
  };

  const ekle = () => {
    if (!ad.trim()) {
      setUyari('Lütfen bir ad girin (ör. "İcra İtirazı").');
      return;
    }
    const s = Number(sure);
    if (!(s >= 1 && s <= 999)) {
      setUyari('Süre 1–999 arasında olmalı.');
      return;
    }
    setUyari('');
    onEkle(ad.trim(), Math.floor(s), birim, rejim);
    setAd('');
    setSure('15');
    setBirim('gun');
    setRejim('kapali');
  };

  return (
    <div>
      <p className="mb-3 text-xs leading-relaxed text-slate-500">
        Yıldızladıklarınız ana ekranda görünür. Kendi sürenizi ekleyebilir; adli tatil rejimini
        (<strong className="text-slate-700">HMK/İYUK +1 hafta</strong> /{' '}
        <strong className="text-slate-700">CMK +3 gün</strong> / kapalı) seçebilirsiniz.
        <strong className="text-slate-700"> Manuel</strong> seçeneği süreyi ana ekrandan elle girmeyi sağlar.
      </p>

      {/* Ekleme paneli */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-3.5">
        <label className="mb-1.5 block text-xs font-semibold text-slate-600">İşlem adı</label>
        <input
          type="text"
          className="app-input"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          placeholder="Örn. İcra İtirazı"
          maxLength={60}
        />

        <label className="mb-1.5 mt-2.5 block text-xs font-semibold text-slate-600">Süre</label>
        <div className="max-w-[260px]">
          <SureSecimi sure={sure} birim={birim} onSureChange={setSure} onBirimChange={setBirim} />
        </div>

        <label className="mb-1.5 mt-2.5 block text-xs font-semibold text-slate-600">Adli tatil</label>
        <AdliTatilSecici value={rejim} onChange={setRejim} />

        <button type="button" onClick={ekle} className="btn-success mt-3 w-full justify-center">
          <FaPlus className="h-3 w-3" /> Hazır süre ekle
        </button>

        {uyari ? (
          <div className="mt-2.5 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800 ring-1 ring-inset ring-amber-200/70">
            {uyari}
          </div>
        ) : null}
      </div>

      {/* Tüm süreler (favori yıldızlı) */}
      <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Süreler <span className="text-slate-400">· yıldız = ana ekranda göster</span>
      </div>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {liste.map((p) => (
          <PresetSatir
            key={p.id}
            p={p}
            favori={favoriler.includes(p.id)}
            onFavori={() => onFavoriTutle(p.id)}
            onSil={p.yerlesik ? null : () => silOnayli(p)}
          />
        ))}
      </ul>
    </div>
  );
}

export default PresetYonetici;
